import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { resolvePageTitle } from "./pageTitleRegistry";

const PageTitleContext = createContext(null);

export const PageTitleProvider = ({ children }) => {
  const { pathname } = useLocation();
  const nextRegistrationId = useRef(0);
  const [override, setOverride] = useState(null);

  const registerPageTitle = useCallback(
    (title) => {
      nextRegistrationId.current += 1;
      const registration = {
        id: nextRegistrationId.current,
        pathname,
        title,
      };
      setOverride(registration);
      return registration.id;
    },
    [pathname],
  );

  const unregisterPageTitle = useCallback((registrationId) => {
    setOverride((current) =>
      current?.id === registrationId ? null : current,
    );
  }, []);

  const value = useMemo(
    () => ({
      pageTitle:
        override?.pathname === pathname
          ? override.title
          : resolvePageTitle(pathname),
      registerPageTitle,
      unregisterPageTitle,
    }),
    [override, pathname, registerPageTitle, unregisterPageTitle],
  );

  return (
    <PageTitleContext.Provider value={value}>
      {children}
    </PageTitleContext.Provider>
  );
};

export const usePageTitle = (title) => {
  const context = useContext(PageTitleContext);
  const registerPageTitle = context?.registerPageTitle;
  const unregisterPageTitle = context?.unregisterPageTitle;

  useEffect(() => {
    if (!registerPageTitle || !unregisterPageTitle || !title) return undefined;

    const registrationId = registerPageTitle(title);
    return () => unregisterPageTitle(registrationId);
  }, [registerPageTitle, title, unregisterPageTitle]);
};

export const useCurrentPageTitle = () =>
  useContext(PageTitleContext)?.pageTitle ?? "";

